import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getValidSelectValue, getZeroOption, type Option, Constants } from '@/src/utils';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
    type ExamOption,
    type FilterState,
} from '../models/filterDTOs';
import {
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleSingleSelect,
    FilterBarContainer,
} from '@/src/ui';

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectGradeOption = getZeroOption('Select Grade');
const NoExamOption = getZeroOption('No Exams Available');
const AllStudentOption = getZeroOption('All');

const REQUIRED_FIELDS = ['districtId', 'schoolId', 'gradeLevelMasterId'] as const;

function getMissingFields(fs: FilterState): string[] {
    return REQUIRED_FIELDS.filter((key) => fs[key] === 0).map((key) => Constants.fieldLabels[key]);
}

const sortByName = <T extends { name: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => a.name.localeCompare(b.name));

const loadingProps = (opts: Option[], val: number, loading: boolean) =>
    loading
        ? { options: [LoadingOption], value: 0, disabled: true }
        : { options: opts, value: val };

interface StudentHistoryFiltersProps {
    refreshTableData: (filters: FilterState) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
    mode: 'normal' | 'student-limited';
}

const StudentHistoryFilters: React.FC<StudentHistoryFiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
    mode,
}) => {
    const isLimited = mode === 'student-limited';

    const [error, setError] = useState('');
    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    // Dropdown options
    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [classOptions, setClassOptions] = useState<Option[]>([AllOption]);
    const [examOptions, setExamOptions] = useState<Option[]>([AllOption]);
    const [studentOptions, setStudentOptions] = useState<Option[]>([AllStudentOption]);

    // Backing data for local filtering
    const [allExamOptions, setAllExamOptions] = useState<ExamOption[]>([]);
    const [classData, setClassData] = useState<ClassOption[]>([]);

    const fetchSchoolClassOptions = useCallback(
        async (districtId: number, gradeLevelMasterId?: number) => {
            if (districtId === 0) {
                setSchoolOptions([SelectSchoolOption]);
                setClassOptions([AllOption]);
                setFilterState((prev) => ({
                    ...prev,
                    schoolId: Number(SelectSchoolOption.id),
                    classId: Number(AllOption.id),
                }));
                return;
            }

            try {
                const res = await apiRequest<SchoolClassOptions>(
                    'GET',
                    `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
                );
                const schools = sortByName(res.data.schoolOptions);
                setSchoolOptions([SelectSchoolOption, ...schools]);

                if (isLimited) {
                    // Auto-select first school
                    const firstSchoolId = schools[0]?.id as number;
                    setFilterState((prev) => ({
                        ...prev,
                        districtId,
                        gradeLevelMasterId: gradeLevelMasterId ?? prev.gradeLevelMasterId,
                        schoolId: firstSchoolId,
                    }));
                } else {
                    const sorted = sortByName(res.data.classOptions);
                    setClassData(sorted);
                    updateClassOptionsFromData(sorted, gradeLevelMasterId);

                    setFilterState((prev) => ({
                        ...prev,
                        schoolId: schools.some((s) => Number(s.id) === Number(prev.schoolId))
                            ? prev.schoolId
                            : Number(SelectSchoolOption.id),
                    }));
                }
            } catch {
                setError('Failed to fetch schools/classes');
                setSchoolOptions([SelectSchoolOption]);
                setClassOptions([AllOption]);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isLimited, setFilterState]
    );

    const fetchExamOptions = useCallback(
        async (gradeId: number, districtId: number, schoolId: number) => {
            if (gradeId === 0) {
                setAllExamOptions([]);
                setExamOptions([AllOption]);
                setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
                return;
            }

            try {
                const res = await apiRequest<{ examOptions: ExamOption[] }>(
                    'GET',
                    `utility/filterData?getExam=true&gradeLevelMasterId=${gradeId}&districtId=${districtId}&schoolId=${schoolId}`
                );
                const exams = sortByName(
                    (res.data.examOptions ?? []).filter((e) => e.schoolIds?.length)
                );
                setAllExamOptions(exams);
                applyExamFilter(exams, filterState.classId);
            } catch {
                setAllExamOptions([]);
                setExamOptions([AllOption]);
                setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
                setError('Failed to fetch exams');
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFilterState, filterState.classId]
    );

    const fetchStudentOptions = useCallback(
        async (classId: number) => {
            if (classId === 0) {
                setStudentOptions([AllStudentOption]);
                setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
                return;
            }

            try {
                const res = await apiRequest<{ studentOptions: Option[] }>(
                    'GET',
                    `utility/filterData?getStudentsByClass=true&classId=${classId}`
                );
                setStudentOptions([AllStudentOption, ...sortByName(res.data.studentOptions ?? [])]);
                setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
            } catch {
                setStudentOptions([AllStudentOption]);
                setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
            }
        },
        [setFilterState]
    );

    /** Derive visible class options from backing classData (normal mode only). */
    const updateClassOptionsFromData = (
        data: ClassOption[] = classData,
        gradeId?: number,
        schoolId?: number
    ) => {
        const effectiveGrade = gradeId ?? filterState.gradeLevelMasterId;
        const effectiveSchool = schoolId ?? filterState.schoolId;

        let filtered = data;

        if (effectiveSchool && effectiveSchool !== 0) {
            filtered = filtered.filter((c) => c.schoolId === effectiveSchool);
        }

        if (effectiveGrade && effectiveGrade !== 0) {
            filtered = filtered.filter(
                (c) =>
                    !c.gradeLevelMasterIds?.length || c.gradeLevelMasterIds.includes(effectiveGrade)
            );
        }

        const mapped = filtered.map((c) => ({ id: c.id, name: c.name }));
        setClassOptions([AllOption, ...mapped]);

        setFilterState((prev) => ({
            ...prev,
            classId: mapped.some((o) => Number(o.id) === Number(prev.classId))
                ? prev.classId
                : Number(AllOption.id),
        }));
    };

    /** Derive visible exam options from allExamOptions, filtered by class. */
    const applyExamFilter = (sourceExams?: ExamOption[], classId?: number) => {
        let filtered = sourceExams ?? [...allExamOptions];

        if (classId && classId !== 0) {
            filtered = filtered.filter((e) => !e.classIds?.length || e.classIds.includes(classId));
        }

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
        } else {
            setExamOptions([AllOption, ...sortByName(filtered)]);
            setFilterState((prev) => ({
                ...prev,
                examId: filtered.some((e) => e.id === prev.examId)
                    ? prev.examId
                    : Number(AllOption.id),
            }));
        }
    };

    /** 1. Fetch initial district + grade options. */
    useEffect(() => {
        (async () => {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true'
                );
                const { gradeLevelMasterOptions: grades, districtOptions: districts } = res.data;
                const sortedDistricts = sortByName(districts);

                if (isLimited) {
                    setGradeLevelMasterOptions(grades);
                    setDistrictOptions(sortedDistricts);

                    // Auto-select first district and grade — school will cascade via effects
                    const firstDistrictId = Number(sortedDistricts[0]?.id);
                    const firstGradeId = Number(grades[0]?.id);

                    setFilterState((prev) => ({
                        ...prev,
                        districtId: firstDistrictId,
                        gradeLevelMasterId: firstGradeId,
                        classId: Number(AllOption.id),
                        examId: Number(AllOption.id),
                        studentId: Number(AllStudentOption.id),
                    }));
                } else {
                    setGradeLevelMasterOptions([SelectGradeOption, ...grades]);
                    setDistrictOptions([SelectDistrictOption, ...sortedDistricts]);

                    setFilterState((prev) => {
                        const isEmpty =
                            !prev.districtId &&
                            !prev.schoolId &&
                            !prev.gradeLevelMasterId &&
                            !prev.classId;

                        return isEmpty
                            ? {
                                  ...prev,
                                  districtId: Number(SelectDistrictOption.id),
                                  schoolId: Number(SelectSchoolOption.id),
                                  gradeLevelMasterId: Number(SelectGradeOption.id),
                                  classId: Number(AllOption.id),
                                  examId: Number(AllOption.id),
                                  studentId: Number(AllStudentOption.id),
                              }
                            : prev;
                    });
                }
            } catch {
                setError('Failed to fetch initial filter options');
            } finally {
                setInitialLoadComplete(true);
            }
        })();
    }, [setFilterState, isLimited]);

    /** 2. When district is set (and initial load done), fetch schools/classes. */
    useEffect(() => {
        if (!initialLoadComplete) return;

        if (filterState.districtId !== 0) {
            fetchSchoolClassOptions(filterState.districtId, filterState.gradeLevelMasterId).then(
                () => setFiltersLoading(false)
            );
        } else {
            setFiltersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete, filterState.districtId]);

    /** 3. Once school is set in student-limited mode, fetch exams for the auto-selected triple. */
    useEffect(() => {
        if (!isLimited || !initialLoadComplete) return;

        const { districtId, schoolId, gradeLevelMasterId } = filterState;
        if (districtId !== 0 && schoolId !== 0 && gradeLevelMasterId !== 0) {
            fetchExamOptions(gradeLevelMasterId, districtId, schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLimited, initialLoadComplete, filterState.schoolId]);

    /** 4. Re-filter exams when class selection changes. */
    useEffect(() => {
        if (allExamOptions.length) {
            applyExamFilter(undefined, filterState.classId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.classId, allExamOptions]);

    // Also hydrate students/exams if we're restoring a filterState that already had a classId
    useEffect(() => {
        if (initialLoadComplete && filterState.classId && filterState.classId !== 0) {
            fetchStudentOptions(filterState.classId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete]);

    const handleDistrictChange = (districtId: number) => {
        setFieldsChanged(true);
        setFilterState((prev) => ({
            ...prev,
            districtId,
            schoolId: Number(SelectSchoolOption.id),
            gradeLevelMasterId: Number(SelectGradeOption.id),
            classId: Number(AllOption.id),
            examId: Number(AllOption.id),
            studentId: Number(AllStudentOption.id),
        }));
        setSchoolOptions([SelectSchoolOption]);
        setClassOptions([AllOption]);
        setExamOptions([AllOption]);
        setStudentOptions([AllStudentOption]);

        if (districtId === 0) {
            fetchSchoolClassOptions(districtId);
            fetchExamOptions(filterState.gradeLevelMasterId, districtId, Number(AllOption.id));
        }
    };

    const handleSchoolChange = (schoolId: number) => {
        setFieldsChanged(true);
        setFilterState((prev) => ({
            ...prev,
            schoolId,
            gradeLevelMasterId: Number(SelectGradeOption.id),
            classId: Number(AllOption.id),
            examId: Number(AllOption.id),
            studentId: Number(AllStudentOption.id),
        }));
        updateClassOptionsFromData(classData, Number(SelectGradeOption.id), schoolId);
    };

    const handleGradeChange = (gradeId: number) => {
        setFieldsChanged(true);
        setFilterState((prev) => ({ ...prev, gradeLevelMasterId: gradeId }));
        updateClassOptionsFromData(classData, gradeId);
        fetchExamOptions(gradeId, filterState.districtId, filterState.schoolId);
    };

    const handleClassChange = (classId: number) => {
        setFieldsChanged(true);
        setFilterState((prev) => ({ ...prev, classId }));
        fetchStudentOptions(classId);
    };

    const handleFilter = () => {
        const missing = getMissingFields(filterState);
        if (missing.length) {
            setError(`Please select: ${missing.join(', ')} before filtering.`);
            return;
        }
        setError('');
        setFieldsChanged(false);
        refreshTableData(filterState);
    };

    const selectSx = { minWidth: 140, maxWidth: 280 };

    if (mode === 'student-limited') return null;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...loadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => handleDistrictChange(Number(e.target.value))}
                    sx={selectSx}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...loadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => handleSchoolChange(Number(e.target.value))}
                    sx={selectSx}
                    disabled={filtersLoading || filterState.districtId === 0}
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...loadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => handleGradeChange(Number(e.target.value))}
                    sx={selectSx}
                    disabled={filtersLoading || filterState.schoolId === 0}
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...loadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => handleClassChange(Number(e.target.value))}
                    sx={selectSx}
                    disabled={
                        filtersLoading ||
                        filterState.districtId === 0 ||
                        filterState.schoolId === 0 ||
                        filterState.gradeLevelMasterId === 0
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment"
                    value={filterState.examId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, examId: Number(e.target.value) }));
                    }}
                    options={examOptions}
                    sx={selectSx}
                />
                <SimpleSingleSelect
                    name="student-select"
                    label="Student"
                    value={filterState.studentId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, studentId: Number(e.target.value) }));
                    }}
                    options={studentOptions}
                    sx={selectSx}
                />
                <SimpleButton
                    onClick={handleFilter}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            {error && <ErrorMessage error={error} />}
        </Box>
    );
};

export default StudentHistoryFilters;
